/**
 * Draw calendar, in Europe/Paris. Two uses:
 *  - the dashboard countdown to the next draw;
 *  - rebuilding the expected calendar to check an ingested history for holes,
 *    which is the completeness control the ingestion pipeline must run.
 */
import type { GameConfig, Weekday } from './games';

const PARIS = 'Europe/Paris';

interface WallClock {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number;
  minute: number;
  second: number;
  /** ISO weekday, 1 = Monday ... 7 = Sunday */
  weekday: Weekday;
}

const WEEKDAY_INDEX: Record<string, Weekday> = {
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
  Sun: 7,
};

const partsFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: PARIS,
  hour12: false,
  weekday: 'short',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

/** What a clock in Paris reads at instant `at`. */
export function parisWallClock(at: Date): WallClock {
  const parts = partsFormatter.formatToParts(at);
  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? '0';
  const hour = Number(get('hour'));
  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
    // Intl can render midnight as 24 with hour12: false.
    hour: hour === 24 ? 0 : hour,
    minute: Number(get('minute')),
    second: Number(get('second')),
    weekday: WEEKDAY_INDEX[get('weekday')] ?? 1,
  };
}

function parisOffsetMs(at: Date): number {
  const w = parisWallClock(at);
  const asUtc = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second);
  return asUtc - Math.floor(at.getTime() / 1000) * 1000;
}

/** The instant at which a Paris clock reads the given wall time. */
export function parisInstant(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
): Date {
  const naive = Date.UTC(year, month - 1, day, hour, minute);
  // Two passes settle DST transitions.
  let offset = parisOffsetMs(new Date(naive));
  offset = parisOffsetMs(new Date(naive - offset));
  return new Date(naive - offset);
}

/** ISO weekday of a YYYY-MM-DD date string, 1 = Monday. */
export function isoWeekday(date: string): Weekday {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return (wd === 0 ? 7 : wd) as Weekday;
}

export function isDrawDay(g: GameConfig, date: string): boolean {
  return g.days.includes(isoWeekday(date));
}

function toIsoDate(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export interface NextDraw {
  /** YYYY-MM-DD of the draw, Paris calendar. */
  date: string;
  /** Instant of the draw itself. */
  at: Date;
  msUntil: number;
  /** True when the draw happens today, Paris time, and has not been held yet. */
  today: boolean;
}

export function nextDraw(g: GameConfig, now: Date = new Date()): NextDraw {
  const w = parisWallClock(now);
  const [hour, minute] = g.drawTime;
  for (let ahead = 0; ahead <= 8; ahead++) {
    const probe = new Date(Date.UTC(w.year, w.month - 1, w.day + ahead));
    const y = probe.getUTCFullYear();
    const m = probe.getUTCMonth() + 1;
    const d = probe.getUTCDate();
    const wd = (probe.getUTCDay() === 0 ? 7 : probe.getUTCDay()) as Weekday;
    if (!g.days.includes(wd)) continue;
    const at = parisInstant(y, m, d, hour, minute);
    if (at.getTime() <= now.getTime()) continue;
    return {
      date: toIsoDate(y, m, d),
      at,
      msUntil: at.getTime() - now.getTime(),
      today: ahead === 0,
    };
  }
  throw new Error(`aucune date de tirage trouvee pour ${g.key}`);
}

/**
 * Every date on which `g` should have been drawn between two dates, inclusive.
 * The ingestion pipeline compares this against what is in the database: an
 * aggregator page that silently returns fewer rows than there were draws is the
 * exact failure mode that cost 55 EuroMillions draws during the first collection.
 */
export function expectedDrawDates(g: GameConfig, from: string, to: string): string[] {
  const [fy, fm, fd] = from.split('-').map(Number) as [number, number, number];
  const [ty, tm, td] = to.split('-').map(Number) as [number, number, number];
  const end = Date.UTC(ty, tm - 1, td);
  const out: string[] = [];
  for (let t = Date.UTC(fy, fm - 1, fd); t <= end; t += 86_400_000) {
    const day = new Date(t);
    const wd = (day.getUTCDay() === 0 ? 7 : day.getUTCDay()) as Weekday;
    if (g.days.includes(wd)) {
      out.push(toIsoDate(day.getUTCFullYear(), day.getUTCMonth() + 1, day.getUTCDate()));
    }
  }
  return out;
}

/** Draw dates expected but absent from `present`. */
export function missingDrawDates(
  g: GameConfig,
  present: readonly string[],
  from: string,
  to: string,
): string[] {
  const have = new Set(present);
  return expectedDrawDates(g, from, to).filter((d) => !have.has(d));
}
